import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Upload, FileText, ShoppingCart, AlertTriangle, CheckCircle2,
  Loader2, Trash2, ArrowRight, FileSpreadsheet, Download,
} from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";

/**
 * B2B bulk-order page. Customer pastes / uploads a list of SKU+qty pairs and
 * gets a validated preview before pushing everything into the cart in one
 * click. Accepts CSV (sku,qty), Excel-pasted TSV, or simple "SKU x qty" lines.
 */
function parseInput(text) {
  /** Best-effort parser. Returns [{ sku, quantity }]. */
  const lines = (text || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const out = [];
  for (const raw of lines) {
    // Skip header rows
    if (/^(sku|artikel|article|item|product|naam|name)[\s,;\t]+/i.test(raw)) continue;
    // Split on comma, tab, semicolon or whitespace+'x'+whitespace
    let parts = raw.split(/\s*[,;\t]\s*/);
    if (parts.length === 1) parts = raw.split(/\s+x\s+/i);
    if (parts.length === 1) parts = raw.split(/\s+/);
    const sku = parts[0]?.trim();
    if (!sku || sku.length < 3) continue;
    const qty = parseInt(parts[1] || "1", 10);
    out.push({ sku, quantity: Number.isFinite(qty) && qty > 0 ? qty : 1 });
  }
  return out;
}

export default function BulkOrderPage() {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [added, setAdded] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (["csv", "txt", "tsv"].includes(ext)) {
      const content = await file.text();
      setText(content);
    } else if (["xlsx", "xlsm"].includes(ext)) {
      // Send to backend for server-side parsing of the official template
      setLoading(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/cart/bulk-resolve-file", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        });
        setPreview(r.data);
        // Sync the textarea so the user sees what was parsed
        const lines = [...r.data.found.map((f) => `${f.sku}, ${f.quantity}`),
                       ...r.data.missing.map((m) => `${m.sku}, ${m.quantity}`)];
        setText(lines.join("\n"));
      } catch (e) {
        setError(e.response?.data?.detail || "Excel-import mislukt — probeer opnieuw.");
      } finally {
        setLoading(false);
      }
    } else {
      setError(`Bestandstype niet ondersteund: .${ext}. Gebruik .csv / .txt / .xlsx of plak Excel-data.`);
    }
  };

  const handleValidate = async () => {
    setError(null);
    setPreview(null);
    const lines = parseInput(text);
    if (lines.length === 0) {
      setError("Geen geldige regels gevonden. Verwacht formaat: SKU,aantal — één per regel.");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/cart/bulk-resolve", { lines });
      setPreview(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Validatie mislukt — probeer opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddAll = () => {
    if (!preview?.found?.length) return;
    preview.found.forEach((it) => {
      addItem({
        product_id: it.product_id,
        sku: it.sku,
        name: it.name,
        image: it.image,
        quantity: it.quantity,
        unit_price: it.applied_price,
        meta: {
          dn: it.dn,
          diameter_mm: it.diameter_mm,
          weight_g: it.weight_g,
          box_qty: it.box_qty,
          box_unit_price: it.box_unit_price,
          piece_price: it.piece_price,
          box_discount_pct: it.box_discount_pct,
          source: "bulk-import",
        },
      });
    });
    setAdded(true);
    setTimeout(() => navigate("/cart"), 900);
  };

  const sample = `304P-7997-½", 50
304P-8000, 50
304P-8006-3/8", 25
316H-8000, 100
316P-8006-3/8", 25`;

  const BASE = process.env.REACT_APP_BACKEND_URL || "";
  const templateUrl = `${BASE}/api/cart/bulk-template.xlsx`;

  return (
    <div className="mx-auto max-w-[1100px] px-5 py-8 lg:py-12" data-testid="bulk-order-page">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-zinc-900 sm:text-4xl">
            Bulk-bestellen <span className="text-nibro-sage-dark">(B2B)</span>
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-zinc-600">
            Plak een SKU+aantal lijst (uit Excel of ERP-export) of upload een .csv bestand.
            We valideren alle SKUs tegen onze catalogus, tonen een prijsoverzicht inclusief
            Omdoos-korting, en voegen daarna alles in één klik aan uw winkelwagen toe.
          </p>
        </div>
        <a
          href={templateUrl}
          download="nibro-bulk-template.xlsx"
          data-testid="bulk-template-download"
          className="inline-flex items-center gap-2 rounded-md border-2 border-nibro-sage-dark bg-white px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-nibro-sage-dark transition hover:bg-nibro-sage-pale"
        >
          <Download size={14} /> Excel-sjabloon (.xlsx)
          <span className="rounded-full bg-nibro-sage-pale px-1.5 py-0.5 text-[10px] text-nibro-sage-dark">1300+ SKUs</span>
        </a>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
        {/* Input column */}
        <div className="space-y-4">
          <div className="rounded-md border border-zinc-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label htmlFor="bulk-text" className="font-display text-sm font-bold text-zinc-800">
                Plak hier uw SKU + aantal lijst
              </label>
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded border border-zinc-200 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wide text-zinc-700 hover:bg-zinc-50">
                <Upload size={12} /> Upload .csv / .xlsx
                <input
                  type="file"
                  accept=".csv,.txt,.tsv,.xlsx,.xlsm,text/csv,text/plain"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="bulk-file-input"
                />
              </label>
            </div>
            <textarea
              id="bulk-text"
              data-testid="bulk-text-input"
              rows={12}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`Voorbeeld:\n${sample}`}
              className="mt-3 w-full rounded border border-zinc-200 bg-zinc-50 p-3 font-mono text-xs text-zinc-900 focus:border-nibro-sage-dark focus:bg-white focus:outline-none"
              spellCheck="false"
            />
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleValidate}
                disabled={loading || !text.trim()}
                data-testid="bulk-validate-button"
                className="inline-flex items-center gap-2 rounded-md bg-aramfix-red px-4 py-2 text-xs font-bold uppercase tracking-wide text-white transition hover:bg-aramfix-red/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                Valideer lijst
              </button>
              <button
                type="button"
                onClick={() => setText(sample)}
                className="text-[11px] font-semibold text-nibro-sage-dark hover:underline"
                data-testid="bulk-sample-link"
              >
                Vul voorbeeldlijst in
              </button>
              <button
                type="button"
                onClick={() => { setText(""); setPreview(null); setError(null); }}
                className="text-[11px] font-semibold text-zinc-500 hover:text-zinc-800"
                data-testid="bulk-clear-link"
              >
                Wis
              </button>
            </div>
            {error && (
              <div className="mt-3 rounded bg-aramfix-red/10 px-3 py-2 text-xs font-semibold text-aramfix-red" data-testid="bulk-error">
                {error}
              </div>
            )}
          </div>

          {preview && (
            <div className="rounded-md border border-zinc-200 bg-white p-5 shadow-sm" data-testid="bulk-preview">
              <h2 className="font-display text-lg font-bold text-zinc-900">
                Voorvertoning
              </h2>
              {preview.found.length > 0 && (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                        <th className="py-2 pr-3">SKU</th>
                        <th className="py-2 pr-3">Omschrijving</th>
                        <th className="py-2 pr-3 text-right">Aantal</th>
                        <th className="py-2 pr-3 text-right">€/stuk</th>
                        <th className="py-2 pr-3 text-right">Totaal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.found.map((it) => (
                        <tr key={it.sku} className="border-t border-zinc-100" data-testid={`bulk-row-${it.sku}`}>
                          <td className="py-2 pr-3 font-mono text-xs font-bold text-zinc-900">{it.sku}</td>
                          <td className="py-2 pr-3 text-xs text-zinc-700">
                            <Link to={`/products/${it.product_id}`} className="hover:underline">
                              {it.name}
                            </Link>
                            {it.dn ? <span className="ml-1 font-mono text-[10px] text-zinc-400">DN{it.dn}</span> : null}
                          </td>
                          <td className="py-2 pr-3 text-right font-mono text-xs">{it.quantity}</td>
                          <td className="py-2 pr-3 text-right">
                            <div className="font-mono text-xs text-zinc-900">€{it.applied_price.toFixed(2)}</div>
                            {it.box_aligned && (
                              <span className="inline-block rounded-full bg-emerald-50 px-1.5 py-0.5 font-mono text-[9px] font-bold text-emerald-700">
                                −{it.box_discount_pct.toFixed(0)}% omdoos
                              </span>
                            )}
                          </td>
                          <td className="py-2 pr-3 text-right font-display text-sm font-bold text-zinc-900">
                            €{it.line_total.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {preview.missing.length > 0 && (
                <div className="mt-4 rounded border border-amber-300 bg-amber-50 p-3 text-xs" data-testid="bulk-missing">
                  <div className="mb-1.5 flex items-center gap-1.5 font-bold uppercase tracking-wide text-amber-800">
                    <AlertTriangle size={12} /> {preview.missing.length} niet-herkende SKU{preview.missing.length === 1 ? "" : "'s"}
                  </div>
                  <ul className="space-y-1 font-mono">
                    {preview.missing.map((m, i) => (
                      <li key={i} className="text-amber-900">
                        <span className="font-bold">{m.sku}</span> × {m.quantity} — {m.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Summary sidebar */}
        <aside className="space-y-4">
          <div className="rounded-md border border-zinc-200 bg-white p-5 shadow-sm">
            <h3 className="font-display text-base font-bold text-zinc-900">Overzicht</h3>
            {preview ? (
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-600">Regels in</span>
                  <span className="font-mono font-bold text-zinc-900">{preview.summary.lines_in}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-600">Herkend</span>
                  <span className="font-mono font-bold text-emerald-700">{preview.summary.lines_found}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-600">Niet herkend</span>
                  <span className={`font-mono font-bold ${preview.summary.lines_missing > 0 ? "text-amber-700" : "text-zinc-400"}`}>{preview.summary.lines_missing}</span>
                </div>
                <div className="flex justify-between border-t border-zinc-200 pt-2">
                  <span className="text-zinc-600">Totaal stuks</span>
                  <span className="font-mono font-bold text-zinc-900">{preview.summary.total_quantity}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-600">Subtotaal</span>
                  <span className="font-display text-lg font-bold text-zinc-900" data-testid="bulk-total-value">€{preview.summary.total_value.toFixed(2)}</span>
                </div>
                <button
                  type="button"
                  onClick={handleAddAll}
                  disabled={preview.summary.lines_found === 0 || added}
                  data-testid="bulk-add-all-button"
                  className={`mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-bold uppercase tracking-wide transition ${
                    added
                      ? "bg-emerald-600 text-white"
                      : preview.summary.lines_found > 0
                      ? "bg-nibro-sage-dark text-white hover:bg-nibro-sage"
                      : "cursor-not-allowed bg-zinc-200 text-zinc-500"
                  }`}
                >
                  {added ? (
                    <><CheckCircle2 size={16} /> Toegevoegd · ga naar winkelwagen</>
                  ) : (
                    <><ShoppingCart size={16} /> Voeg {preview.summary.lines_found} regels toe <ArrowRight size={14} /></>
                  )}
                </button>
              </div>
            ) : (
              <p className="mt-2 text-xs text-zinc-500">Valideer een lijst om het overzicht en de totalen te zien.</p>
            )}
          </div>

          <div className="rounded-md border border-zinc-200 bg-zinc-50 p-4 text-xs text-zinc-700">
            <div className="mb-2 flex items-center gap-1.5 font-bold uppercase tracking-wide text-zinc-800">
              <FileSpreadsheet size={12} /> Ondersteunde formaten
            </div>
            <ul className="space-y-1.5 leading-relaxed">
              <li><a href={templateUrl} download className="font-bold text-nibro-sage-dark hover:underline" data-testid="bulk-sidebar-template-link">Excel-sjabloon downloaden</a> (bevat alle 1300+ SKUs)</li>
              <li><span className="font-mono font-bold">SKU,aantal</span> (CSV uit Excel)</li>
              <li><span className="font-mono font-bold">SKU [tab] aantal</span> (geplakt uit Excel)</li>
              <li><span className="font-mono font-bold">SKU x aantal</span> (informeel)</li>
              <li>Aantal weglaten = 1 stuk</li>
            </ul>
            <p className="mt-3 text-[11px] text-zinc-500">
              Prijzen worden live uitgerekend; bij ≥{" "}
              <strong>volle omdoos-aantallen</strong> wordt automatisch de omdoos-korting toegepast.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
